(ns quuppa.peweb.query
  "Provides API to make queries to the backend, and handle the
  app-db state management for the query results.

  ## Query declaration:

  ## Execute

  ## Events

  - `update-event-fn [query f]`
      Use this function to create rf/reg-event-db compatible function, that will update state of the given query.
      State is updated to result of (f old-state rf-evt).


  ## Subs

  subscribe takes the query

  Example how `subscribe` can be used in rf/reg-sub input signal fn:

    (rf/reg-sub :qsm/qpe-feature-supported
      (fn [[_ qpe-identity]]
        (q/subscribe authorized-qpes {} [:result qpe-identity :features]))
      (fn [features [_ _qpe-identity feature-k]]
        (contains? features feature-k)))

  ## Co-effects

  Use `::execute` fx like this:

    {:fx [[::q/execute [sites-query {:project-id 123}]]]}

  "
  (:require [re-frame.core :as rf]
            ["react" :as react]
            [quuppa.re-frame-utils :as rf-u]))

(defn params-tuple [this params]
  (mapv #(get params %) (:param-keys this)))

;;
;; Query API
;;

(rf/reg-sub ::query-result-root
  (fn [db _]
    (::state db)))

;; Query-result is map of :result :loading

(rf-u/reg-recursive-sub ::query-result-raw [::query-result-root])

(rf/reg-sub ::query-result
  (fn [[_ query-id params]]
    (rf/subscribe (into [::query-result-raw query-id] params)))
  (fn [query-status [_ _query-id _params]]
    ;; Also show loading status if query hasn't been executed yet.
    (merge {:loading true
            :initial-load true}
           query-status)))

(rf/reg-sub ::query-result-ks
  (fn [[_ query-id params _ks]]
    (rf/subscribe [::query-result query-id params]))
  (fn [query-status [_ _ _ ks]]
    (get-in query-status ks)))

(defn get-result [db query params]
  (-> (::state db)
      (get (:query-id query))
      (get-in (params-tuple query params))))

(defn- update-state [db query-id p f & args]
  (if (seq p)
    (apply update-in db [::state query-id] update-in p f args)
    (apply update-in db [::state query-id] f args)))

(rf/reg-event-fx
  ::execute
  (fn [{:keys [db]} [_ query-id params p opts]]
    (let [evt {:event [query-id params]
               :on-success [:query/result query-id p opts]
               :on-error [:query/error query-id p opts]}]
      (when (every? some? p)
        {:fx [[:server-dispatch evt]]
         :db (update-state db query-id p (fn [x]
                                           (assoc x
                                                  :loading true
                                                  :initial-load (nil? x))))}))))

;; WS callback handler
;; TODO: If response is received after query is cleaned, ignore response?
(rf/reg-event-fx
  :query/result
  (fn [{:keys [db]} [_ query-id p opts res]]
    {:db (update-state db query-id p assoc
                       :loading false
                       :initial-load false
                       :error nil
                       :result res)
     :fx [(when (:on-success opts)
            [:dispatch (conj (:on-success opts) res)])]}))

(rf/reg-event-fx
  :query/error
  (fn [{:keys [db]} [_ query-id p _opts error]]
    {:db (update-state db query-id p assoc
                       :loading false
                       :initial-load false
                       :error error
                       :result nil)}))

(rf/reg-event-db
  ::clean-query
  (fn [db [_ query-id p]]
    ;; TODO: dissoc-in
    (update db ::state update-in query-id dissoc p)))

;;
;; "Simple API"
;; The query object abstracts away re-frame event and
;; subscriptions parameters etc.
;;

(defn subscribe
  ([this]
   (subscribe this {}))
  ([this params]
   (rf/subscribe [::query-result (:query-id this) (params-tuple this params)]))
  ([this params ks]
   (rf/subscribe [::query-result-ks (:query-id this) (params-tuple this params) ks])))

(defn execute
  ([this params]
   (execute this params nil))
  ([this params opts]
   (rf/dispatch [::execute (:query-id this) params (params-tuple this params) opts])))

(defn clean [this params]
  (rf/dispatch [::clean-query (:query-id this) (params-tuple this params)]))

(defn create-query
  "query-id: server side WS endpoint id
   param-keys: seq of keys that query is interested from the 'response'"
  [query-id param-keys]
  {:query-id query-id
   :param-keys param-keys})

(rf/reg-fx ::execute
  (fn [[query params opts]]
    (execute query params opts)))

(defn <-signal-fn
  "Create signal function to be used in rf/reg-sub, based on given query's data."
  ([query] (<-signal-fn query {}))
  ([query params]
   (fn pe-query-signal-fn [_rf-query-vec]
     (subscribe query params)))
  ([query params ks]
   (fn pe-query-signal-fn [_rf-query-vec]
     (subscribe query params ks))))

;;
;; Optional hook integration
;;

(defn use-query
  "React Hook with requests data for given query and params on mount,
  returns results via Re-frame subscription and cleans the data from app-db
  on unmount."
  ([query params]
   (use-query query params nil))
  ([query params _ #_{:keys [clean?]
                      :or {clean? false}}]
   (react/useEffect
     (fn []
       (execute query params nil)
       ;; If clean method is only used by QMD module, module splitting doesn't work?
       ; (when clean?
       ;   (fn []
       ;     (clean query params)))
       )
     (clj->js (params-tuple query params)))
   @(subscribe query params)))

(defn update-state-db [db query f event]
  (update-state db (:query-id query) (conj (params-tuple query event) :result) f event))

(defn update-event-fn
  "Returns function for reg-event-db, that will update the :result of given query, by calling (f current-result event).
  NOTE: the event must contain the parameters tuple that the query is interested in!"
  [query f]
  (fn [db [_ event]]
    (update-state-db db query f event)))
